import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { COMPANY_INFO, NAVIGATION_LINKS } from "../data/siteData";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleAnchorClick = (e, targetId) => {
    if (location.pathname === "/") {
      e.preventDefault();
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
        window.history.pushState(null, "", `/#${targetId}`);
      }
    }
    setMobileMenuOpen(false);
  };

  const handleOfferClick = (e) => {
    setMobileMenuOpen(false);

    // Look for form or calculator element on current page
    const formElement =
      document.getElementById("tilbud") ||
      document.getElementById("hero-kontakt") ||
      document.getElementById("kontakt") ||
      document.getElementById("beregner");

    if (formElement) {
      e.preventDefault();
      formElement.scrollIntoView({ behavior: "smooth", block: "start" });
      if (window.history.pushState) {
        window.history.pushState(null, "", `${location.pathname}#tilbud`);
      }
      const firstInput = formElement.querySelector(
        "input:not([type=hidden]):not([name=website_url]), select, textarea, button"
      );
      if (firstInput && typeof firstInput.focus === "function") {
        setTimeout(() => firstInput.focus({ preventScroll: true }), 400);
      }
    }
  };

  return (
    <>
      {/* Main Sticky Header */}
      <header
        className={`sticky top-0 z-50 bg-white transition-[box-shadow,border-color] duration-200 py-3 sm:py-3.5 ${
          scrolled
            ? "shadow-md shadow-slate-900/5 border-b border-slate-200"
            : "border-b border-slate-100"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 rounded-lg py-0.5">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=95,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/logo.png"
              alt="NF-Polering Logo"
              className="w-auto object-contain transition-transform duration-200 group-hover:scale-105 h-14 sm:h-16 lg:h-18 xl:h-20"
            />
          </Link>

          {/* Desktop Navigation (Visible only on 1200px and up) */}
          <nav className="hidden min-[1200px]:flex items-center gap-2 xl:gap-3" aria-label="Hovedmenu">
            {/* Forside */}
            <Link
              to="/"
              className={`px-2.5 xl:px-3 py-1.5 text-sm xl:text-[15px] font-semibold transition-colors ${
                isActive("/") && location.pathname === "/"
                  ? "text-slate-950 font-bold"
                  : "text-slate-700 hover:text-slate-950"
              }`}
            >
              Forside
            </Link>

            {/* Vinduespudsning */}
            <Link
              to="/vinduespudsning"
              className={`px-2.5 xl:px-3 py-1.5 text-sm xl:text-[15px] font-semibold transition-colors ${
                isActive("/vinduespudsning")
                  ? "text-slate-950 font-bold"
                  : "text-slate-700 hover:text-slate-950"
              }`}
            >
              Vinduespudsning
            </Link>

            {/* Solcellevask */}
            <Link
              to="/solcellevask"
              className={`px-2.5 xl:px-3 py-1.5 text-sm xl:text-[15px] font-semibold transition-colors ${
                isActive("/solcellevask")
                  ? "text-slate-950 font-bold"
                  : "text-slate-700 hover:text-slate-950"
              }`}
            >
              Solcellevask
            </Link>

            {/* Fliserens */}
            <Link
              to="/fliserens"
              className={`px-2.5 xl:px-3 py-1.5 text-sm xl:text-[15px] font-semibold transition-colors ${
                isActive("/fliserens")
                  ? "text-slate-950 font-bold"
                  : "text-slate-700 hover:text-slate-950"
              }`}
            >
              Fliserens
            </Link>

            {/* Dækningsområder */}
            <Link
              to="/daekningsomraader"
              className={`px-2.5 xl:px-3 py-1.5 text-sm xl:text-[15px] font-semibold transition-colors ${
                isActive("/daekningsomraader") || isActive("/byer")
                  ? "text-slate-950 font-bold"
                  : "text-slate-700 hover:text-slate-950"
              }`}
            >
              Dækningsområder
            </Link>

            {/* Om os */}
            <Link
              to="/om-os"
              className={`px-2.5 xl:px-3 py-1.5 text-sm xl:text-[15px] font-semibold transition-colors ${
                isActive("/om-os")
                  ? "text-slate-950 font-bold"
                  : "text-slate-700 hover:text-slate-950"
              }`}
            >
              Om os
            </Link>

            {/* Kontakt */}
            <Link
              to="/kontakt"
              className={`px-2.5 xl:px-3 py-1.5 text-sm xl:text-[15px] font-semibold transition-colors ${
                isActive("/kontakt")
                  ? "text-slate-950 font-bold"
                  : "text-slate-700 hover:text-slate-950"
              }`}
            >
              Kontakt
            </Link>
          </nav>

          {/* Right Header CTAs */}
          <div className="flex items-center gap-2.5 sm:gap-3">
            {/* Phone Button */}
            <a
              href={COMPANY_INFO.phoneTel}
              className="hidden min-[480px]:inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-slate-300 text-slate-800 hover:border-slate-400 hover:bg-slate-50 font-semibold text-sm transition-all whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-cyan-600" aria-hidden="true" />
              <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
            </a>

            {/* Få et tilbud CTA */}
            <Link
              to="/#tilbud"
              onClick={handleOfferClick}
              className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-[#113151] hover:bg-[#0b2238] text-white font-bold text-sm sm:text-base transition-all whitespace-nowrap shadow-sm hover:shadow cursor-pointer"
            >
              <span>Få et tilbud</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>

            {/* Mobile / Tablet Burger Trigger (Visible strictly under 1200px) */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="flex min-[1200px]:hidden p-1.5 text-slate-900 focus:outline-none bg-transparent select-none cursor-pointer"
              aria-label={mobileMenuOpen ? "Luk menu" : "Åbn menu"}
            >
              {mobileMenuOpen ? (
                <i className="fa-solid fa-xmark text-2xl" aria-hidden="true" />
              ) : (
                <i className="fa-solid fa-bars text-2xl" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay (Visible strictly under 1200px when opened) */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 min-[1200px]:hidden bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="fixed inset-y-0 right-0 max-w-sm w-full bg-white shadow-2xl p-6 overflow-y-auto flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                  <img
                    src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=95,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/logo.png"
                    alt="NF-Polering Logo"
                    className="h-12 sm:h-14 w-auto object-contain"
                  />
                </Link>
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-1.5 text-slate-900 focus:outline-none bg-transparent cursor-pointer"
                  aria-label="Luk menu"
                >
                  <i className="fa-solid fa-xmark text-xl" aria-hidden="true" />
                </button>
              </div>

              <div className="py-4 space-y-1.5">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 text-lg font-semibold ${
                    location.pathname === "/"
                      ? "text-slate-950 font-bold"
                      : "text-slate-700"
                  }`}
                >
                  Forside
                </Link>

                {/* Vinduespudsning */}
                <Link
                  to="/vinduespudsning"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 text-lg font-semibold ${
                    location.pathname === "/vinduespudsning"
                      ? "text-slate-950 font-bold"
                      : "text-slate-700"
                  }`}
                >
                  Vinduespudsning
                </Link>

                {/* Solcellevask */}
                <Link
                  to="/solcellevask"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 text-lg font-semibold ${
                    location.pathname === "/solcellevask"
                      ? "text-slate-950 font-bold"
                      : "text-slate-700"
                  }`}
                >
                  Solcellevask
                </Link>

                <Link
                  to="/fliserens"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 text-lg font-semibold ${
                    location.pathname === "/fliserens"
                      ? "text-slate-950 font-bold"
                      : "text-slate-700"
                  }`}
                >
                  Fliserens
                </Link>

                <Link
                  to="/daekningsomraader"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 text-lg font-semibold ${
                    location.pathname === "/daekningsomraader" || location.pathname === "/byer"
                      ? "text-slate-950 font-bold"
                      : "text-slate-700"
                  }`}
                >
                  Dækningsområder
                </Link>

                <Link
                  to="/om-os"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 text-lg font-semibold ${
                    location.pathname === "/om-os"
                      ? "text-slate-950 font-bold"
                      : "text-slate-700"
                  }`}
                >
                  Om os
                </Link>

                <Link
                  to="/kontakt"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-3 py-2 text-lg font-semibold ${
                    location.pathname === "/kontakt"
                      ? "text-slate-950 font-bold"
                      : "text-slate-700"
                  }`}
                >
                  Kontakt
                </Link>
              </div>
            </div>

            {/* Bottom Contact Drawer Info */}
            <div className="pt-6 border-t border-slate-100 space-y-3">
              <a
                href={COMPANY_INFO.phoneTel}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-slate-900 text-white font-semibold text-sm hover:bg-slate-800 transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
                <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
              </a>

              <Link
                to="/#tilbud"
                onClick={handleOfferClick}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-[#113151] hover:bg-[#0b2238] text-white font-bold text-sm transition-colors whitespace-nowrap shadow-sm cursor-pointer"
              >
                <span>Få et tilbud</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>

              <div className="text-center text-xs text-slate-500 pt-2 space-y-1">
                <p className="whitespace-nowrap">CVR: {COMPANY_INFO.cvr}</p>
                <p className="whitespace-nowrap">{COMPANY_INFO.email}</p>
                <p>{COMPANY_INFO.address}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
